import type { CSSProperties } from 'react';
import { useMemo } from 'react';
import { formatFigure, phaseColor } from '@/lib/phase';
import { buildTurns } from '@/session/debug';
import { useSession } from '@/session/store';
import { BudgetBar } from './BudgetBar';
import { Waterfall } from './Waterfall';

export function Turns({ newestFirst }: { newestFirst: boolean }) {
  const debug = useSession((state) => state.debug);

  const turns = useMemo(() => buildTurns(debug), [debug]);
  const ordered = newestFirst ? [...turns].reverse() : turns;

  return (
    <>
      <div className="dbg-cols" aria-hidden="true">
        <span />
        <span>event</span>
        <span>span</span>
        <span>+prev</span>
        <span>elapsed</span>
      </div>

      <div className="dbg-scroll">
        {ordered.length === 0 ? (
          <p className="dbg-empty">
            No telemetry yet. Only the Andromeda backend emits debug frames — against a gateway that
            does not, the frame log is the record.
          </p>
        ) : (
          ordered.map((turn) => (
            <article key={turn.index} className="dbg-turn">
              <header className="flex items-baseline justify-between gap-2">
                <span className="dbg-turn-no">{turn.index}</span>
                <span className="flex flex-col items-end gap-[5px]">
                  <span className="dbg-turn-label">user finish → first speech</span>
                  {turn.totalMs === null ? (
                    <span className="dbg-turn-total-pending">in flight</span>
                  ) : (
                    <span className="dbg-turn-total">
                      {formatFigure(turn.totalMs)}
                      <i className="dbg-unit">ms</i>
                    </span>
                  )}
                </span>
              </header>

              {turn.segments.length > 0 && (
                <>
                  <BudgetBar segments={turn.segments} />
                  <dl className="dbg-stages">
                    {turn.stages.map((stage, index) => (
                      <div
                        key={`${stage.label}-${index}`}
                        className="flex items-baseline gap-1.5"
                        style={{ '--c': phaseColor(stage.phase) } as CSSProperties}
                      >
                        <dt className="dbg-stage-name">{stage.label}</dt>
                        <dd className="dbg-stage-value">
                          {formatFigure(stage.ms)}
                          <i className="dbg-unit">ms</i>
                        </dd>
                      </div>
                    ))}
                  </dl>
                </>
              )}

              <Waterfall events={turn.events} />
            </article>
          ))
        )}
      </div>
    </>
  );
}
